import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { REMOVAL_REASON_LABELS, RemovalReason, RemovalRequest } from "../interfaces/DTO/removal";

interface Props {
  open: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: (request: RemovalRequest) => Promise<void>;
}

/**
 * Taking back tasks / a simulation - the agent gives the reason, the backend decides by what already happened:
 * deleted (nothing left the office), cancelled and kept with the reason (the client saw / signed), or left as is.
 */
export const RemovalDialog: React.FC<Props> = ({ open, title, onCancel, onConfirm }) => {
  const [reason, setReason] = useState<RemovalReason>(RemovalReason.CLIENT_CHANGED_MIND);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(RemovalReason.CLIENT_CHANGED_MIND);
      setNote("");
    }
  }, [open]);

  const noteMissing = reason === RemovalReason.OTHER && !note.trim();

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm({ reason, note: note.trim() || undefined });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} dir="rtl" fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Alert severity="info">
          מה שעוד לא יצא מהמשרד יימחק. מה שהלקוח כבר ראה או חתם עליו יבוטל וישמר עם הסיבה (לתיעוד מול רשות שוק ההון).
          מה שכבר נשלח ליצרן לא ישתנה.
        </Alert>
        <FormControl fullWidth size="small">
          <InputLabel>סיבה</InputLabel>
          <Select
            value={reason}
            label="סיבה"
            onChange={(e) => setReason(e.target.value as RemovalReason)}
            // the menu opens in a portal outside the dialog's dir="rtl"
            MenuProps={{ PaperProps: { sx: { direction: "rtl" } } }}
          >
            {Object.values(RemovalReason).map((r) => (
              <MenuItem key={r} value={r}>
                {REMOVAL_REASON_LABELS[r]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={reason === RemovalReason.OTHER ? "פירוט (חובה)" : "פירוט (אופציונלי)"}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          size="small"
          inputProps={{ maxLength: 1000 }}
          error={noteMissing}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          חזרה
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={busy || noteMissing}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          אישור
        </Button>
      </DialogActions>
    </Dialog>
  );
};
